/** @jsxImportSource ./simboluri-jsx */
import { GammaRdRand } from "./tipuri";
import { useT } from "../i18n";

// γ_Rd, așa cum îl folosesc Pașii 13 și 14. Nu e o setare: fiecare verificare
// de calcul capabil îl citește din normativ, după clasa de ductilitate aleasă
// la Pasul 3, la fiecare rulare. Tabelul se pune în ambele filele de armare ca
// să nu existe două lecturi ale aceleiași cifre.

// 1.2 → «1,20» — cifrele din interfață sunt în convenția românească
export function zecGamma(x: unknown) {
  const n = Number(x);
  return Number.isFinite(n) ? n.toFixed(2).replace(".", ",") : String(x ?? "—");
}

export default function GammaRdTabel({
  randuri,
  clasa,
}: {
  randuri?: GammaRdRand[];
  clasa?: string | number;
}) {
  const t = useT();
  if (!randuri || randuri.length === 0) return null;
  const cls = randuri[0].clasa ?? clasa;
  const sist = randuri[0].sistem;
  const alt = randuri[0].sistem_alt;
  const nume = (s?: string) =>
    s === "EC8" ? "SR EN 1998-1" : s === "P100" ? "P100-1/2013" : s;
  const difera = randuri.filter((v) => v.gamma_rd_alt !== v.gamma_rd).length;

  return (
    <div className="st-findings">
      <h3>
        {t("struct.gamma.titlu")}
        <span className="sg-muted">
          {t("struct.gamma.sub", { sistem: String(nume(sist) ?? ""), clasa: String(cls ?? "") })}
        </span>
      </h3>
      <p className="sg-muted">
        {t("struct.gamma.explicatie")}{" "}
        <code>surse_legislative/P100-1-2013_valori.md</code>.
        {alt && (
          <>
            {t("struct.gamma.coloana")}
            <strong>{nume(alt)}</strong>
            {t("struct.gamma.pe_aceeasi_clasa")}
            {difera === 0
              ? t("struct.gamma.nu_difera")
              : t("struct.gamma.difera", { n: difera, total: randuri.length })}
            {t("struct.gamma.sistem_nota")}
          </>
        )}
      </p>
      <table className="sg-table">
        <thead>
          <tr>
            <th>{t("struct.gamma.col_verificare")}</th>
            <th style={{ textAlign: "right" }}>γ_Rd</th>
            {alt && (
              <th style={{ textAlign: "right" }} className="sg-muted">
                {nume(alt)}
              </th>
            )}
            <th>{t("struct.gamma.col_unde")}</th>
            <th>{t("struct.gamma.col_sursa")}</th>
          </tr>
        </thead>
        <tbody>
          {randuri.map((v) => (
            <tr key={v.cheie}>
              <td>{v.titlu}</td>
              <td style={{ textAlign: "right", fontWeight: 600 }}>
                {v.gamma_rd_baza !== undefined &&
                v.gamma_rd_baza !== v.gamma_rd ? (
                  <>
                    {zecGamma(v.gamma_rd_baza)} / {zecGamma(v.gamma_rd)}
                    <div className="sg-muted" style={{ fontWeight: 400 }}>
                      baza / restul
                    </div>
                  </>
                ) : (
                  zecGamma(v.gamma_rd)
                )}
              </td>
              {alt && (
                <td
                  className="sg-muted"
                  style={{
                    textAlign: "right",
                    fontWeight: v.gamma_rd_alt !== v.gamma_rd ? 600 : 400,
                  }}
                >
                  {v.gamma_rd_alt !== undefined ? zecGamma(v.gamma_rd_alt) : "—"}
                </td>
              )}
              <td className="sg-muted">
                {v.formula}
                {v.nota && v.gamma_rd_baza !== v.gamma_rd && (
                  <div style={{ marginTop: 2 }}>{v.nota}</div>
                )}
              </td>
              <td className="sg-muted">{v.sursa}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
